import { WebshopProductModel } from "models/WebShop";
import { useEffect, useMemo, useState } from "react";
import { webShopService } from "services/Services";
import LoadingScreen from "../LoadingScreen";
import { Link, useParams } from "react-router-dom";
import { UrlPath } from "utility/routes";
import ProductPromo from "../ProductPromo";
import { useOrders } from "context/useOrderData";
import { useCompanySaleCheck } from "context/useCompanySaleCheck";
import { Seo } from "../Seo";
import BoughtAllProductsView from "./BoughtAllProductsView";


type ProductListProps = {
    displayExtra?: boolean
}

const ProductList = ({ displayExtra }: ProductListProps) => {
    const params = useParams();
    const companyId = params.id ?? "";
    const { companySaleCheck } = useCompanySaleCheck();
    const { saleStatusByCompany } = useOrders();
    const [products, setProducts] = useState<WebshopProductModel[] | undefined>();
    const [loadingProducts, setLoadingProducts] = useState<boolean>(true);

    const [alreadyBought, setAlreadyBought] = useState<Record<string, boolean>>({});

    useEffect(() => {
        async function getData() {
            const productsList = await webShopService.getProducts();
            if (productsList !== undefined) {
                setProducts(productsList);
            }

            const configs = await webShopService.getProductConfigurations(params.id ?? "");

            if (configs !== undefined) {

                setAlreadyBought({
                    [UrlPath.Account.ProductConfig.Logo]: configs.logo !== null,
                    [UrlPath.Account.ProductConfig.BulletPoints]: configs.bulletPoints !== null,
                    [UrlPath.Account.ProductConfig.InfoText]: configs.infoText !== null,
                    [UrlPath.Account.ProductConfig.ExtraLinks]: configs.extraLinks !== null,
                });

            }

            setLoadingProducts(false);
        }

        getData();
    }, []);

    const boughtAll = useMemo(
        () => Object.values(alreadyBought).every(Boolean),
        [alreadyBought]);


    const saleInfo = companySaleCheck[companyId];

    if (!saleInfo?.allowed) return null;


    const activeProductIdSet = new Set(
        saleStatusByCompany[companyId].orderStatuses.flatMap(status =>
            status.activeProducts.map(product => product.id)
        )
    );

    const filteredProducts = params.product
        ? products?.filter(product => product.path !== params.product)
        : products;


    if (loadingProducts) return displayExtra ? <LoadingScreen /> : null;

    if (boughtAll) { return displayExtra ? <BoughtAllProductsView /> : null }

    return (
        <>
            {filteredProducts && filteredProducts.length > 0 ? (
                <div className="card mt-3">
                    <div className="card-body">

                        {displayExtra ? (
                            <>
                                <Seo title="Legg til ekstra informasjon om bedriften" />
                                <h1 className="h3">Legg til ekstra informasjon om bedriften</h1>
                            </>
                        ) : (
                            <h2 className="h5">Legg til ekstra informasjon om bedriften</h2>
                        )}

                        <ul className="cc-shop-items list-unstyled">
                            {filteredProducts.map(product => {
                                const isActive = activeProductIdSet.has(product.webShopProductId);

                                return (
                                    <ProductPromo
                                        key={product.path}
                                        name={product.name}
                                        path={product.path}
                                        companyId={companyId}
                                        description={product.description}
                                        linkText={product.linkText}
                                        alreadyBought={alreadyBought[product.path]}
                                        isActive={isActive}
                                    />
                                );
                            })}
                        </ul>
                        {displayExtra && (
                            <p className="mt-4 mb-0">
                                <Link to={UrlPath.MyAccount.Home} className="btn btn-outline-primary">
                                    <i className="bi bi-arrow-left"></i> Tilbake
                                </Link>
                            </p>
                        )}
                    </div>
                </div>
            ) : null
            }
        </>

    );


};

export default ProductList;
